import { useContext, useEffect, useState } from 'react';

import MediaLightbox from '@atom/components/common/mediaLightbox/MediaLightbox';
import PaginationControl from '@atom/components/common/PaginationControl';
import textConstants from '@atom/constants/textConstants';
import { Snackbar } from '@atom/mui';
import { Media } from '@atom/types/media';
import { PolicyAction } from '@atom/types/policy';
import { hasAccess } from '@atom/utilities/accessUtilities';

import InventoryDetailContext from '../InventoryDetailContext';

import PhotosHeader from './PhotosHeader';
import PhotoTile from './PhotoTile';
import useAssetPhotos from './useAssetPhotos';
import usePhotosPageState, {
  RESULTS_PER_PAGE_OPTIONS,
} from './usePhotosPageState';

import classes from './photosTab.module.css';

const PhotosTab = () => {
  const { asset, updateAsset } = useContext(InventoryDetailContext);

  const [lightboxMediaId, setLightboxMediaId] = useState<string>(null);
  const [deletingMediaId, setDeletingMediaId] = useState<string>(null);

  const {
    page,
    limit,
    search,
    selectedIds,
    onPageChange,
    onLimitChange,
    onSearchChange,
    onSelectionToggle,
    onSelectionChange,
  } = usePhotosPageState();

  const {
    media,
    totalCount,
    mediaLoading,
    uploadPhotos,
    renamePhoto,
    deletePhoto,
    deletePhotos,
    downloadPhotos,
    refetchPhotos,
  } = useAssetPhotos({ assetId: asset.id, page, limit, search });

  const canCreateMedia = hasAccess(PolicyAction.CREATE_MEDIA, asset.actions);
  const canUpdateMedia = hasAccess(PolicyAction.UPDATE_MEDIA, asset.actions);
  const canDeleteMedia = hasAccess(PolicyAction.DELETE_MEDIA, asset.actions);
  const canMarkAsCoverPhoto = hasAccess(PolicyAction.UPDATE, asset.actions);

  useEffect(() => {
    if (!mediaLoading && !media.length && page > 1) {
      onPageChange(page - 1);
    }
  }, [mediaLoading, media.length, page]);

  const onCoverPhotoToggle = async (mediaId: string) => {
    const selected = media.find(medium => medium.id === mediaId);

    try {
      await updateAsset({
        id: asset.id,
        mainPhotoFileId:
          asset.mainPhotoFileId === selected?.fileId ? '' : selected?.fileId,
      });
    } catch (error) {
      Snackbar.error({ message: textConstants.GENERIC_APPLICATION_ERROR_TEXT });
    }
  };

  const onDeletePhoto = async (id: string) => {
    Snackbar.info({ message: textConstants.GENERIC_APPLICATION_DELETING_TEXT });
    setDeletingMediaId(id);

    try {
      await deletePhoto(id);
      onSelectionChange([]);

      Snackbar.info({ message: 'Successfully deleted file' });
    } catch (error) {
      Snackbar.error({ message: 'Failed to delete file' });
    } finally {
      setDeletingMediaId(null);
    }
  };

  const onDeleteFromLightbox = async (id: string) => {
    setLightboxMediaId(null);
    await onDeletePhoto(id);
  };

  const onDeleteSelected = async () => {
    try {
      await deletePhotos([...selectedIds]);
      onSelectionChange([]);
    } catch (error) {
      Snackbar.error({ message: 'Failed to delete files' });
    }
  };

  const onDownloadSelected = async () => {
    await downloadPhotos(media.filter(medium => selectedIds.has(medium.id)));
  };

  return (
    <div className={classes.tabContent}>
      <div className={classes.leftBodyPaneFixed} />
      <div className={classes.rightBodyPaneFill}>
        <PhotosHeader
          photoCount={media.length}
          selectedCount={selectedIds.size}
          search={search}
          loading={mediaLoading}
          canCreateMedia={canCreateMedia}
          canDeleteMedia={canDeleteMedia}
          onSearchChange={onSearchChange}
          onUpload={uploadPhotos}
          onDownloadSelected={onDownloadSelected}
          onDeleteSelected={onDeleteSelected}
          onSelectAll={() => onSelectionChange(media.map(medium => medium.id))}
          onDeselectAll={() => onSelectionChange([])}
        />
        <div className={classes.photoBlock}>
          <div className={classes.photoTilesContainer}>
            {media.map((medium: Media) => (
              <PhotoTile
                key={medium.id}
                media={medium}
                isCoverPhoto={asset.mainPhotoFileId === medium.fileId}
                isSelected={selectedIds.has(medium.id)}
                deleteLoading={deletingMediaId === medium.id}
                canMarkAsCoverPhoto={canMarkAsCoverPhoto}
                canUpdateMedia={canUpdateMedia}
                canDeleteMedia={canDeleteMedia}
                onOpen={setLightboxMediaId}
                onSelectionToggle={onSelectionToggle}
                onRename={renamePhoto}
                onDelete={onDeletePhoto}
                onCoverPhotoToggle={onCoverPhotoToggle}
              />
            ))}
          </div>
        </div>
        {!!media.length && (
          <div className={classes.paginationContainer}>
            <PaginationControl
              incrementPage={increment => onPageChange(page + increment)}
              updateLimit={newLimit => onLimitChange(Number(newLimit))}
              limitOptions={RESULTS_PER_PAGE_OPTIONS}
              page={page}
              limit={limit}
              total={totalCount}
            />
          </div>
        )}
      </div>
      <MediaLightbox
        open={!!lightboxMediaId}
        media={media}
        selectedMediaId={lightboxMediaId}
        coverPhotoFileId={asset.mainPhotoFileId}
        canMarkAsCoverPhoto={canMarkAsCoverPhoto}
        canUpdateMedia={canUpdateMedia}
        canDeleteMedia={canDeleteMedia}
        onClose={() => setLightboxMediaId(null)}
        onSelectedMediaChange={setLightboxMediaId}
        onRename={renamePhoto}
        onDelete={onDeleteFromLightbox}
        onCoverPhotoToggle={onCoverPhotoToggle}
        onRefetch={refetchPhotos}
      />
    </div>
  );
};

export default PhotosTab;
